import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Building2, User, Users, ArrowRight, Globe, Check } from 'lucide-react';
import { toast } from 'sonner';

const DEMO_CREDENTIALS = [
  {
    role: 'Agency Admin',
    email: 'admin@cherryholidays.com',
    password: 'Demo@2026',
    icon: Building2,
    color: 'bg-blue-500',
  },
  {
    role: 'Staff',
    email: 'staff@cherryholidays.com',
    password: 'Demo@2026',
    icon: User,
    color: 'bg-green-500',
  },
  {
    role: 'B2B Agent',
    email: 'b2bagent@india.com',
    password: 'Demo@2026',
    icon: Users,
    color: 'bg-amber-500',
  }
];

// Demo credentials are shown only on the public demo tenant (homepage "View Demo").
const DEMO_TENANT_SLUG = 'cherry-holidays';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { tenant, tenantSlug, buildPath } = useTenant();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login successful!');
      // Use the tenant_slug from login response to redirect to correct tenant
      // This ensures users are redirected to their own tenant, not the current URL's tenant
      if (result.tenant_slug) {
        navigate(`/${result.tenant_slug}/dashboard`);
      } else if (result.redirect_url) {
        navigate(result.redirect_url);
      } else {
        navigate(buildPath('/dashboard'));
      }
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const companyName = tenant?.company_name || 'Super Package Creator';
  const logoUrl = tenant?.logo_url;
  const primaryColor = tenant?.primary_color || '#1e40af';
  const secondaryColor = tenant?.secondary_color || '#3b82f6';
  const loginBgColor = tenant?.login_background_color || '#0f172a';

  // Helper function to determine if a color is light or dark
  const isLightColor = (hexColor) => {
    if (!hexColor) return false;
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  // Determine text colors based on background brightness
  const bgIsLight = isLightColor(loginBgColor);
  const textColor = bgIsLight ? '#1f2937' : '#ffffff';
  const subtextColor = bgIsLight ? '#4b5563' : '#cbd5e1';

  // Create dynamic gradient style - more prominent gradient
  const brandGradient = {
    background: `linear-gradient(135deg, ${loginBgColor} 0%, ${primaryColor} 50%, ${secondaryColor} 100%)`,
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={brandGradient}
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div 
            className="absolute top-20 left-20 w-72 h-72 rounded-full filter blur-3xl opacity-30"
            style={{ backgroundColor: secondaryColor }}
          ></div>
          <div 
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full filter blur-3xl opacity-30"
            style={{ backgroundColor: primaryColor }}
          ></div>
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full filter blur-3xl opacity-20"
            style={{ backgroundColor: '#ffffff' }}
          ></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">
          <div className="mb-8">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-16 object-contain" />
            ) : (
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                style={{ 
                  background: `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor})`,
                  boxShadow: `0 10px 15px -3px ${primaryColor}30`
                }}
              >
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: textColor }}>
            {companyName}
          </h1>
          <p className="text-xl mb-8 max-w-md" style={{ color: subtextColor }}>
            Create stunning travel quotations in minutes. Manage hotels, rates, and packages effortlessly.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3" style={{ color: subtextColor }}>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: bgIsLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}
              >
                <Check className="w-5 h-5" style={{ color: bgIsLight ? '#16a34a' : '#4ade80' }} />
              </div>
              <span>Multi-tenant SaaS Platform</span>
            </div>
            <div className="flex items-center gap-3" style={{ color: subtextColor }}>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: bgIsLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}
              >
                <Check className="w-5 h-5" style={{ color: bgIsLight ? '#16a34a' : '#4ade80' }} />
              </div>
              <span>Hotel Rate Management</span>
            </div>
            <div className="flex items-center gap-3" style={{ color: subtextColor }}>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: bgIsLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}
              >
                <Check className="w-5 h-5" style={{ color: bgIsLight ? '#16a34a' : '#4ade80' }} />
              </div>
              <span>PDF & Email Quotations</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-slate-50 min-w-0">
        <div className="w-full max-w-md min-w-0">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-12 mx-auto object-contain" />
            ) : (
              <div 
                className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor})`
                }}
              >
                <Building2 className="w-7 h-7 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-slate-900 mt-4">{companyName}</h1>
          </div>

          <Card className="border-0 shadow-xl bg-white" data-testid="login-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-[#0a192f]">Welcome back</CardTitle>
              <CardDescription className="text-[#0a192f]/60">
                Sign in to access your dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form" autoComplete="off">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#0a192f]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="off"
                    className="h-11 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 focus:bg-white"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#0a192f]">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    className="h-11 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 focus:bg-white"
                    data-testid="password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02]"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
              
              {/* Forgot Password Link */}
              <div className="text-center">
                <Link 
                  to={buildPath('/forgot-password')} 
                  className="text-sm text-cyan-600 hover:text-teal-600 font-medium"
                >
                  Forgot your password?
                </Link>
              </div>

              {(tenantSlug || '').toLowerCase() === DEMO_TENANT_SLUG && (
              <div className="pt-4 border-t border-[#0a192f]/10" data-testid="demo-credentials-section">
                <p className="text-sm font-medium text-[#0a192f]/70 mb-3">Demo Credentials</p>
                <div className="space-y-3 bg-slate-50 rounded-lg p-4">
                  {DEMO_CREDENTIALS.map((cred, index) => {
                    const Icon = cred.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`w-8 h-8 ${cred.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#0a192f] text-sm">{cred.role}</p>
                          <p className="text-xs text-[#0a192f]/60 mt-0.5 break-words">
                            <span className="font-medium">Email:</span> {cred.email}
                          </p>
                          <p className="text-xs text-[#0a192f]/60 break-words">
                            <span className="font-medium">Password:</span> {cred.password}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="flex-shrink-0 text-xs border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                          onClick={() => {
                            setEmail(cred.email);
                            setPassword(cred.password);
                            toast.success(`Credentials filled for ${cred.role}`);
                          }}
                          data-testid={`demo-fill-${cred.role.toLowerCase().replace(' ', '-')}`}
                        >
                          <ArrowRight className="w-3 h-3 mr-1" />
                          Sign in
                        </Button>
                      </div>
                    );
                  })}
                </div>
                
                {/* Back to Main Site Link - for tenant URLs */}
                <div className="mt-4 pt-3 border-t border-[#0a192f]/10">
                  <Link 
                    to="/" 
                    className="flex items-center justify-center gap-2 text-sm text-[#0a192f]/50 hover:text-[#0a192f]/70 transition-colors"
                    data-testid="back-to-main-site-link"
                  >
                    <Globe className="w-4 h-4" />
                    <span>Back to Main Site</span>
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#0a192f]/60 mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-cyan-600 hover:text-teal-600 font-medium">
              Register your agency
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
